import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import Loader from "@/ui/atoms/Loader";

import GridLoader from "@/ui/molecules/GridLoader";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
import LayoutFullScreenOverlay from "@/ui/layouts/LayoutFullScreenOverlay";

import type {
    DealExtractionInfo,
    ExtractionType,
} from "@/ui/organisms/dealTypes";
import type { DealGridState } from "@/ui/organisms/DealTable";
import type { UserDatabase, UserType } from "@/ui/organisms/userTypes";
import { DealTable } from "@/ui/organisms/DealTable";
import SearchBar from "@/ui/molecules/SearchBar";
import { Userpilot } from "userpilot";

import type {
    ViewFormattedDateProps,
    ViewFormattedDateParams,
} from "@/ui/views/ViewFormattedDate";
import { createViewFormattedDate } from "@/ui/views/ViewFormattedDate";

export const SEARCH_BAR_PLACEHOLDER =
    "Search by Project Name or Property Address";
export const VIEW_DEAL_LIST_ACTION_BAR_HEIGHT = 47 + 16 + 16;

export type ViewDealListParams = {
    useState: () => {
        userRegion: ReturnType<
            ViewFormattedDateParams["useState"]
        >["userRegion"];
        selectedDatabases: UserDatabase[] | null;
        dealExtractionInfo: DealExtractionInfo[];
        showDatabaseSelectionModal: boolean;
        savedGridState?: DealGridState;
        projectsIsPending: boolean;
        projectsIsError: boolean;
        saveGridStatus: "idle" | "resolved" | "rejected";
        dealSearchPhrase: string;
        userType: UserType | undefined;
    };
    useCases: () => {
        deleteProjectMutation: {
            mutateAsync: (params: {
                projectId: number;
                databaseId: number;
            }) => Promise<void>;
            isPending: boolean;
        };
        deleteExtractionMutation: {
            mutateAsync: (params: {
                projectId: number;
                databaseId: number;
                extractionIds: number[];
            }) => Promise<void>;
            isPending: boolean;
        };
        exportExtraction: ({
            extractions,
            projectId,
            databaseId,
        }: {
            extractions: {
                extractionId: number;
                extractionType: ExtractionType;
            }[];
            projectId: number;
            databaseId: number;
        }) => void;
        setShowDatabaseSelectionModal: (showModal: boolean) => void;
        setDealCreationDatabaseId: (databaseId: number) => void;
        resetViewButtonClicked: () => void;
        saveGridState: (gridState: DealGridState) => void;
        setDealSearchPhrase: (query: string) => void;
        resolveRentRollLocation: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => Promise<{ excelsiorFileId: number; sheetId: number } | null>;
    };
};

export type ViewDealListProps = {
    getDealUrl: ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => string;
    getExtractionUrl: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => string;
    getRentRollExtractionUrl: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => string;
    handleCreateDealButtonClicked: (databaseId: number) => void;
    handleDealViewButtonClicked: ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => void;
    handleViewMappingButtonClick?: () => void;
    handleViewExtractionButtonClick: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => void;
    handleDraftResumeButtonClicked: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
    }) => void;
    handleAddFileButtonClicked: (params: {
        projectId: number;
        databaseId: number;
    }) => void;
} & Omit<BoxProps, "children">;

export const createViewDealList = ({
    useState,
    useCases,
}: ViewDealListParams) => {
    const ViewFormattedDate = createViewFormattedDate({
        useState,
    });
    const ComponentFormattedDate = (props: {
        date: ViewFormattedDateProps["date"];
    }) => {
        return (
            <ViewFormattedDate
                date={props.date}
                parts={["day", "month", "year", "hour", "minute", "second"]}
            />
        );
    };

    const ViewDealList = (props: ViewDealListProps) => {
        const {
            sx,
            getDealUrl,
            getExtractionUrl,
            getRentRollExtractionUrl,
            handleCreateDealButtonClicked,
            handleDealViewButtonClicked,
            handleViewMappingButtonClick,
            handleViewExtractionButtonClick,
            handleDraftResumeButtonClicked,
            handleAddFileButtonClicked,
            ...otherProps
        } = props;

        const {
            dealExtractionInfo,
            selectedDatabases,
            showDatabaseSelectionModal,
            savedGridState,
            projectsIsPending,
            projectsIsError,
            saveGridStatus,
            dealSearchPhrase,
            userType,
        } = useState();

        const {
            deleteProjectMutation,
            deleteExtractionMutation,
            exportExtraction,
            setShowDatabaseSelectionModal,
            setDealCreationDatabaseId,
            resetViewButtonClicked,
            saveGridState,
            setDealSearchPhrase,
            resolveRentRollLocation,
        } = useCases();

        // Overview rent-roll rows lack file identity; resolve it then navigate.
        const handleViewRentRoll = async ({
            projectId,
            databaseId,
            extractionId,
        }: {
            projectId: number;
            databaseId: number;
            extractionId: number;
        }) => {
            const location = await resolveRentRollLocation({
                projectId,
                databaseId,
                extractionId,
            });
            // Resolution failed (a danger toast was already shown); don't navigate.
            if (!location) return;
            window.open(
                getRentRollExtractionUrl({
                    fileId: location.excelsiorFileId,
                    extractionId: Math.abs(extractionId),
                    sheetId: `s${location.sheetId}`,
                    databaseId,
                }),
                "_self",
            );
        };
        const createDealClicked = () => {
            if (!selectedDatabases) return;
            if (selectedDatabases.length === 1 && selectedDatabases[0]) {
                setDealCreationDatabaseId(selectedDatabases[0].databaseId);
                handleCreateDealButtonClicked(selectedDatabases[0].databaseId);
            } else {
                setShowDatabaseSelectionModal(true);
            }
            Userpilot.track("odin_user_clicks_create_deal");
        };

        const showLoadingOverlay =
            deleteProjectMutation.isPending ||
            deleteExtractionMutation.isPending;

        const isInitialLoading =
            projectsIsPending && dealExtractionInfo.length === 0;

        return (
            <>
                {showLoadingOverlay && (
                    <LayoutFullScreenOverlay>
                        <LayoutFillParentCentered>
                            <Loader />
                        </LayoutFillParentCentered>
                    </LayoutFullScreenOverlay>
                )}
                <Box
                    sx={{
                        ...sx,
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                    }}
                    {...otherProps}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            paddingBottom: "16px",
                            paddingTop: "16px",
                            paddingRight: "2rem",
                            boxSizing: "border-box",
                            minHeight: `${VIEW_DEAL_LIST_ACTION_BAR_HEIGHT}px`,
                        }}
                    >
                        {handleViewMappingButtonClick && (
                            <Box
                                onClick={handleViewMappingButtonClick}
                                component="button"
                                sx={{
                                    marginRight: "10px",
                                    border: "1px solid var(--proda-black)",
                                    borderRadius: "5px",
                                    padding: "10px",
                                    backgroundColor: "var(--proda-white)",
                                    fontWeight: 600,
                                    "&:hover": {
                                        cursor: "pointer",
                                    },
                                }}
                            >
                                View Mapping
                            </Box>
                        )}
                        <SearchBar
                            sx={{
                                "& input": {
                                    height: "43px",
                                },
                                width: "20vw",
                                marginRight: "1rem",
                            }}
                            placeholder={SEARCH_BAR_PLACEHOLDER}
                            value={dealSearchPhrase}
                            onChange={setDealSearchPhrase}
                        />
                        <Box
                            onClick={createDealClicked}
                            component="button"
                            sx={{
                                border: "1px solid var(--proda-black)",
                                borderRadius: "5px",
                                padding: "10px",
                                backgroundColor: "var(--primary-color)",
                                color: "var(--proda-white)",
                                fontWeight: 600,
                            }}
                        >
                            Create Project
                        </Box>
                    </Box>
                    {isInitialLoading ? (
                        <LayoutFillParentCentered>
                            <GridLoader />
                        </LayoutFillParentCentered>
                    ) : (
                        <DealTable
                            getRentRollExtractionUrl={getRentRollExtractionUrl}
                            suppressNoRowOverlay={showDatabaseSelectionModal}
                            rowData={dealExtractionInfo}
                            dealsIsError={projectsIsError}
                            savedGridState={savedGridState}
                            onViewDeal={handleDealViewButtonClicked}
                            onViewExtraction={handleViewExtractionButtonClick}
                            onViewRentRoll={params =>
                                void handleViewRentRoll(params)
                            }
                            onResumeDraft={handleDraftResumeButtonClicked}
                            onAddFile={handleAddFileButtonClicked}
                            onDealDelete={params =>
                                deleteProjectMutation.mutateAsync(params)
                            }
                            onExtractionDelete={params =>
                                deleteExtractionMutation.mutateAsync({
                                    projectId: params.projectId,
                                    databaseId: params.databaseId,
                                    extractionIds: [params.extractionId],
                                })
                            }
                            onExport={exportExtraction}
                            resetViewButtonClicked={resetViewButtonClicked}
                            saveGridState={saveGridState}
                            saveGridStatus={saveGridStatus}
                            getDealUrl={getDealUrl}
                            getExtractionUrl={getExtractionUrl}
                            dealSearchPhrase={dealSearchPhrase}
                            ComponentFormattedDate={ComponentFormattedDate}
                            userType={userType}
                        />
                    )}
                </Box>
            </>
        );
    };

    return ViewDealList;
};
